import React, { useEffect, useState, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { createPortal } from "react-dom";
import {
  FaEye,
  FaPrint,
  FaFileExcel,
  FaFilePdf,
  FaTimes,
} from "react-icons/fa";
import {
  getTasksReport,
  clearTasksReport,
  clearError,
  clearSuccess,
  getProjectsForReport,
  getEmployeesForReport,
  getContractorsForReport,
} from "../../../../redux/Slices/ProjectReportsSlice";
import { useTitle } from "../../../../shared/hooks/TitleContext";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import * as XLSX from "xlsx";
import i18n from "../../../../i18n/i18n";
import { getPdfRowStyles, chunkArray } from "../../../../shared/utils.js/utils";

const TasksReport = () => {
  const dispatch = useDispatch();
  const { setTitle } = useTitle();
  const { t } = useTranslation("global");
  const isRTL = i18n.language === "ar";
  const dir = isRTL ? "rtl" : "ltr";
  const headerAlign = isRTL ? "right" : "left";
  const publicUrl = process.env.PUBLIC_URL || "";
  const headerLogo = `${publicUrl}/ACE.png`;

  const {
    tasksReport,
    projects,
    employees,
    contractors,
    isLoading,
    error,
    success,
  } = useSelector(state => state.projectReports);

  const getProjectName = project => {
    if (!project) return "";
    return isRTL
      ? project.name_ar || project.name_en || project.name || ""
      : project.name_en || project.name || project.name_ar || "";
  };

  const getEmployeeName = employee => {
    if (!employee) return "";
    return isRTL
      ? employee.name_ar || employee.name_en || employee.name || ""
      : employee.name_en || employee.name || employee.name_ar || "";
  };

  const getContractorName = contractor => {
    if (!contractor) return "";
    return isRTL
      ? contractor.name_ar || contractor.name_en || contractor.name || ""
      : contractor.name_en || contractor.name || contractor.name_ar || "";
  };

  const translateTaskStatus = value => {
    switch (value) {
      case "pending":
        return t("projectReports.pending", { defaultValue: "Pending" });
      case "in_progress":
        return t("projectReports.inProgress", { defaultValue: "In Progress" });
      case "completed":
      case "done":
        return t("projectReports.completed", { defaultValue: "Completed" });
      case "cancelled":
        return t("projectReports.cancelled", { defaultValue: "Cancelled" });
      default:
        return value;
    }
  };

  const translateTaskPriority = value => {
    switch (value) {
      case "low":
        return t("projectReports.low", { defaultValue: "Low" });
      case "medium":
        return t("projectReports.medium", { defaultValue: "Medium" });
      case "high":
        return t("projectReports.high", { defaultValue: "High" });
      case "critical":
        return t("projectReports.critical", { defaultValue: "Critical" });
      default:
        return value;
    }
  };

  const [projectId, setProjectId] = useState("");
  const [employeeId, setEmployeeId] = useState("");
  const [contractorId, setContractorId] = useState("");
  const [status, setStatus] = useState("");
  const [priority, setPriority] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");

  const [projectDropdownOpen, setProjectDropdownOpen] = useState(false);
  const [projectSearchTerm, setProjectSearchTerm] = useState("");
  const [projectPage, setProjectPage] = useState(1);
  const projectSearchInputRef = useRef(null);

  const [employeeDropdownOpen, setEmployeeDropdownOpen] = useState(false);
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState("");
  const [employeePage, setEmployeePage] = useState(1);
  const employeeSearchInputRef = useRef(null);

  const [contractorDropdownOpen, setContractorDropdownOpen] = useState(false);
  const [contractorSearchTerm, setContractorSearchTerm] = useState("");
  const [contractorPage, setContractorPage] = useState(1);
  const contractorSearchInputRef = useRef(null);

  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [priorityDropdownOpen, setPriorityDropdownOpen] = useState(false);

  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [pdfType, setPdfType] = useState(null);
  const [pdfChunks, setPdfChunks] = useState([]); // For paginated PDF generation
  const [allTasksForPreview, setAllTasksForPreview] = useState([]); // All tasks for preview
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewZoom, setPreviewZoom] = useState(1.0);
  const [previewPosition, setPreviewPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const pageSize = 18;
  const projectsHasNext =
    Array.isArray(projects) && projects.length >= pageSize;
  const employeesHasNext =
    Array.isArray(employees) && employees.length >= pageSize;
  const contractorsHasNext =
    Array.isArray(contractors) && contractors.length >= pageSize;

  useEffect(() => {
    setTitle("projectReports.tasks", { defaultValue: "تقارير المهام" });
    dispatch(getProjectsForReport({ page: 1 }));
    dispatch(getEmployeesForReport({ page: 1 }));
    dispatch(getContractorsForReport({ page: 1 }));
    dispatch(getTasksReport({}));
    return () => {
      dispatch(clearTasksReport());
    };
  }, [dispatch, setTitle]);

  useEffect(() => {
    if (success) {
      if (isInitialLoad) {
        setIsInitialLoad(false);
        dispatch(clearSuccess());
        return;
      }
      dispatch(clearSuccess());
    }
  }, [success, dispatch, isRTL, isInitialLoad]);

  useEffect(() => {
    if (error) {
      toast.error(error, { rtl: isRTL });
      setTimeout(() => dispatch(clearError()), 2500);
    }
  }, [error, dispatch, isRTL]);

  useEffect(() => {
    const handleMouseMove = e => {
      if (!isDragging) return;
      setPreviewPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging, dragStart]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (projectSearchTerm) {
        dispatch(getProjectsForReport({ search: projectSearchTerm, page: 1 }));
        setProjectPage(1);
      } else {
        dispatch(getProjectsForReport({ page: projectPage }));
      }
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [projectSearchTerm, dispatch, projectPage]);

  useEffect(() => {
    if (projectDropdownOpen && !projectSearchTerm) {
      dispatch(getProjectsForReport({ page: projectPage }));
    }
  }, [projectPage, projectDropdownOpen, projectSearchTerm, dispatch]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (employeeSearchTerm) {
        dispatch(
          getEmployeesForReport({ search: employeeSearchTerm, page: 1 }),
        );
        setEmployeePage(1);
      } else {
        dispatch(getEmployeesForReport({ page: employeePage }));
      }
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [employeeSearchTerm, dispatch, employeePage]);

  useEffect(() => {
    if (employeeDropdownOpen && !employeeSearchTerm) {
      dispatch(getEmployeesForReport({ page: employeePage }));
    }
  }, [employeePage, employeeDropdownOpen, employeeSearchTerm, dispatch]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (contractorSearchTerm) {
        dispatch(
          getContractorsForReport({ search: contractorSearchTerm, page: 1 }),
        );
        setContractorPage(1);
      } else {
        dispatch(getContractorsForReport({ page: contractorPage }));
      }
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [contractorSearchTerm, dispatch, contractorPage]);

  useEffect(() => {
    if (contractorDropdownOpen && !contractorSearchTerm) {
      dispatch(getContractorsForReport({ page: contractorPage }));
    }
  }, [contractorPage, contractorDropdownOpen, contractorSearchTerm, dispatch]);

  useEffect(() => {
    const handleClickOutside = e => {
      if (
        projectDropdownOpen &&
        !e.target.closest(".project-dropdown-container")
      ) {
        setProjectDropdownOpen(false);
      }
      if (
        employeeDropdownOpen &&
        !e.target.closest(".employee-dropdown-container")
      ) {
        setEmployeeDropdownOpen(false);
      }
      if (
        contractorDropdownOpen &&
        !e.target.closest(".contractor-dropdown-container")
      ) {
        setContractorDropdownOpen(false);
      }
      if (
        statusDropdownOpen &&
        !e.target.closest(".status-dropdown-container")
      ) {
        setStatusDropdownOpen(false);
      }
      if (
        priorityDropdownOpen &&
        !e.target.closest(".priority-dropdown-container")
      ) {
        setPriorityDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [
    projectDropdownOpen,
    employeeDropdownOpen,
    contractorDropdownOpen,
    statusDropdownOpen,
    priorityDropdownOpen,
  ]);

  const filteredProjects = useMemo(() => {
    if (!Array.isArray(projects)) return [];
    if (!projectSearchTerm) return projects;
    const term = projectSearchTerm.toLowerCase();
    return projects.filter(project => {
      const nameAr = project.name_ar?.toLowerCase() || "";
      const nameEn =
        project.name_en?.toLowerCase() || project.name?.toLowerCase() || "";
      const code = project.code?.toLowerCase() || "";
      return (
        nameAr.includes(term) || nameEn.includes(term) || code.includes(term)
      );
    });
  }, [projects, projectSearchTerm]);

  const filteredEmployees = useMemo(() => {
    if (!Array.isArray(employees)) return [];
    if (!employeeSearchTerm) return employees;
    const term = employeeSearchTerm.toLowerCase();
    return employees.filter(emp => {
      const nameAr = emp.name_ar?.toLowerCase() || "";
      const nameEn =
        emp.name_en?.toLowerCase() || emp.name?.toLowerCase() || "";
      const code = emp.code?.toLowerCase() || "";
      return (
        nameAr.includes(term) || nameEn.includes(term) || code.includes(term)
      );
    });
  }, [employees, employeeSearchTerm]);

  const filteredContractors = useMemo(() => {
    if (!Array.isArray(contractors)) return [];
    if (!contractorSearchTerm) return contractors;
    const term = contractorSearchTerm.toLowerCase();
    return contractors.filter(con => {
      const nameAr = con.name_ar?.toLowerCase() || "";
      const nameEn =
        con.name_en?.toLowerCase() || con.name?.toLowerCase() || "";
      const code = con.code?.toLowerCase() || "";
      return (
        nameAr.includes(term) || nameEn.includes(term) || code.includes(term)
      );
    });
  }, [contractors, contractorSearchTerm]);

  const handleSubmit = e => {
    e.preventDefault();
    const params = {};
    if (projectId) params.project_id = projectId;
    if (employeeId) params.employee_id = employeeId;
    if (contractorId) params.contractor_id = contractorId;
    if (status) params.status = status;
    if (priority) params.priority = priority;
    if (fromDate) params.from_date = fromDate;
    if (toDate) params.to_date = toDate;
    if (sortBy) params.sort_by = sortBy;
    if (sortOrder) params.sort_order = sortOrder;
    dispatch(getTasksReport(params));
  };

  const handlePreviewAll = async () => {
    if (!tasksReport) {
      toast.error(
        t("projectReports.noData", { defaultValue: "لا توجد بيانات للمعاينة" }),
        { rtl: isRTL },
      );
      return;
    }

    // Get current report data to extract total
    const currentData = tasksReport?.data || tasksReport || {};
    const currentTasks = currentData.data || currentData.tasks || [];
    const total =
      currentData.total || currentData.meta?.total || currentTasks.length;

    if (total === 0 || currentTasks.length === 0) {
      toast.error(
        t("projectReports.noData", { defaultValue: "لا توجد بيانات للمعاينة" }),
        { rtl: isRTL },
      );
      return;
    }

    // Fetch all tasks with per_page = total
    const params = {
      page: 1,
      per_page: total,
    };
    if (projectId) params.project_id = projectId;
    if (employeeId) params.employee_id = employeeId;
    if (contractorId) params.contractor_id = contractorId;
    if (status) params.status = status;
    if (priority) params.priority = priority;
    if (fromDate) params.from_date = fromDate;
    if (toDate) params.to_date = toDate;
    if (sortBy) params.sort_by = sortBy;
    if (sortOrder) params.sort_order = sortOrder;

    try {
      const result = await dispatch(getTasksReport(params));
      if (getTasksReport.fulfilled.match(result)) {
        const allData = result.payload?.data || result.payload || {};
        const allTasks = allData.data || allData.tasks || [];
        setAllTasksForPreview(allTasks);
        setIsPreviewOpen(true);
        setPreviewZoom(1.0);
        setPreviewPosition({ x: 0, y: 0 });
      } else {
        toast.error(
          t("projectReports.printError", {
            defaultValue: "فشل تحميل البيانات",
          }),
          { rtl: isRTL },
        );
      }
    } catch (err) {
      toast.error(
        t("projectReports.printError", { defaultValue: "فشل تحميل البيانات" }),
        { rtl: isRTL },
      );
    }
  };

  const handlePrintAll = async () => {
    try {
      setIsGeneratingPdf(true);

      // Get current report data to extract total
      const currentData = tasksReport?.data || tasksReport || {};
      const currentTasks = currentData.data || currentData.tasks || [];
      const total =
        currentData.total || currentData.meta?.total || currentTasks.length;

      if (total === 0 || currentTasks.length === 0) {
        toast.error(
          t("projectReports.noData", {
            defaultValue: "لا توجد بيانات للطباعة",
          }),
          { rtl: isRTL },
        );
        setIsGeneratingPdf(false);
        return;
      }

      // Fetch all tasks with per_page = total
      const params = {
        page: 1,
        per_page: total,
      };
      if (projectId) params.project_id = projectId;
      if (employeeId) params.employee_id = employeeId;
      if (contractorId) params.contractor_id = contractorId;
      if (status) params.status = status;
      if (priority) params.priority = priority;
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      if (sortBy) params.sort_by = sortBy;
      if (sortOrder) params.sort_order = sortOrder;

      const result = await dispatch(getTasksReport(params));
      if (!getTasksReport.fulfilled.match(result)) {
        toast.error(
          t("projectReports.printError", {
            defaultValue: "فشل تحميل البيانات",
          }),
          { rtl: isRTL },
        );
        setIsGeneratingPdf(false);
        return;
      }

      const allData = result.payload?.data || result.payload || {};
      const allTasks = allData.data || allData.tasks || [];

      if (!allTasks || allTasks.length === 0) {
        toast.error(
          t("projectReports.noData", {
            defaultValue: "لا توجد بيانات للطباعة",
          }),
          { rtl: isRTL },
        );
        setIsGeneratingPdf(false);
        return;
      }

      // Split data into chunks of 18 items per page
      // Each chunk will be displayed on a separate page
      const chunks = chunkArray(allTasks, 18);
      setPdfChunks(chunks);

      setPdfType("all");

      // Wait for React to render the templates
      await new Promise(resolve => requestAnimationFrame(resolve));
      await new Promise(resolve => requestAnimationFrame(resolve));
      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      }
      await new Promise(resolve => setTimeout(resolve, 800));

      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const devicePixelRatio = window.devicePixelRatio || 1;
      const scale = Math.min(2, Math.max(1.5, devicePixelRatio));

      // Generate PDF for each chunk (each chunk = one page with max 18 items)
      for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
        // Wait for DOM update - multiple frames to ensure rendering
        await new Promise(resolve => requestAnimationFrame(resolve));
        await new Promise(resolve => requestAnimationFrame(resolve));
        await new Promise(resolve => setTimeout(resolve, 100));

        const element = document.getElementById(
          `tasks-report-pdf-all-${chunkIndex}`,
        );
        if (!element) {
          if (chunkIndex === 0) {
            setPdfChunks([]);
            toast.error(
              t("projectReports.printError", {
                defaultValue: "قالب الطباعة غير متوفر",
              }),
              { rtl: isRTL },
            );
            setIsGeneratingPdf(false);
            setPdfType(null);
            return;
          }
          continue;
        }

        const canvas = await html2canvas(element, {
          scale,
          useCORS: true,
          logging: false,
          backgroundColor: "#ffffff",
          letterRendering: false,
          allowTaint: false,
          removeContainer: false,
          imageTimeout: 10000,
          onclone: clonedDoc => {
            const clonedElement = clonedDoc.getElementById(
              `tasks-report-pdf-all-${chunkIndex}`,
            );
            if (clonedElement) {
              clonedElement.style.fontFamily =
                '"Times New Roman Bold", "Times New Roman", "Times", serif';
              clonedElement.style.fontWeight = "bold";
              clonedElement.style.direction = dir;
            }
          },
        });

        const imgData = canvas.toDataURL("image/png", 1.0);
        const imgHeight = (canvas.height * pdfWidth) / canvas.width;

        if (chunkIndex > 0) {
          pdf.addPage();
        }

        // Add the image to the current page
        pdf.addImage(
          imgData,
          "PNG",
          0,
          0,
          pdfWidth,
          imgHeight,
          undefined,
          "FAST",
        );
      }

      pdf.save(`ACE_TasksReport_${new Date().toISOString().split("T")[0]}.pdf`);
      toast.success(
        t("projectReports.printed", { defaultValue: "تم طباعة التقرير بنجاح" }),
        { rtl: isRTL },
      );

      // Restore
      setPdfChunks([]);
    } catch (err) {
      console.error("Tasks Report PDF error:", err);
      toast.error(
        t("projectReports.printError", {
          defaultValue: "حدث خطأ أثناء إنشاء PDF",
        }),
        { rtl: isRTL },
      );
    } finally {
      setIsGeneratingPdf(false);
      setPdfType(null);
      setPdfChunks([]);
    }
  };

  const handleExportAllToExcel = () => {
    if (!tasksReport) {
      toast.error(
        t("projectReports.noData", { defaultValue: "لا توجد بيانات للتصدير" }),
        { rtl: isRTL },
      );
      return;
    }

    try {
      const data = tasksReport?.data || tasksReport || {};
      const tasks = data.data || data.tasks || [];

      const excelData = [
        {
          [t("projectReports.period", { defaultValue: "الفترة" })]:
            `${formatDate(fromDate)} - ${formatDate(toDate)}`,
        },
        {},
        ...tasks.map(task => ({
          [t("projectReports.taskNo", { defaultValue: "Task No" })]:
            task.id || "—",
          [t("projectReports.taskName", { defaultValue: "Task Name" })]: isRTL
            ? task.name_ar || task.name_en
            : task.name_en || task.name_ar || "—",
          [t("projectReports.project", { defaultValue: "المشروع" })]: isRTL
            ? task.project?.name_ar || task.project?.name_en
            : task.project?.name_en || task.project?.name_ar,
          [t("projectReports.employee", { defaultValue: "الموظف" })]: isRTL
            ? task.employee?.name_ar || task.employee?.name_en
            : task.employee?.name_en || task.employee?.name_ar || "—",
          [t("projectReports.contractor", { defaultValue: "المقاول" })]: isRTL
            ? task.contractor?.name_ar || task.contractor?.name_en
            : task.contractor?.name_en || task.contractor?.name_ar || "—",
          [t("projectReports.status", { defaultValue: "الحالة" })]:
            translateTaskStatus(task.status) || "—",
          [t("projectReports.priority", { defaultValue: "الأولوية" })]:
            translateTaskPriority(task.priority) || "—",
          [t("projectReports.startDate", { defaultValue: "Start Date" })]:
            formatDate(task.start_date),
          [t("projectReports.endDate", { defaultValue: "End Date" })]:
            formatDate(task.end_date),
        })),
      ];

      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Tasks Report");
      XLSX.writeFile(
        workbook,
        `TasksReport_${new Date().toISOString().split("T")[0]}.xlsx`,
      );
      toast.success(
        t("projectReports.exported", {
          defaultValue: "تم تصدير البيانات بنجاح",
        }),
        { rtl: isRTL },
      );
    } catch (err) {
      toast.error(
        err.message ||
          t("projectReports.exportError", {
            defaultValue: "فشل في تصدير البيانات",
          }),
        { rtl: isRTL },
      );
    }
  };

  const formatDate = dateString => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  };

  const reportData = tasksReport?.data || tasksReport || {};
  const tasks = reportData.data || reportData.tasks || [];
  const summary = reportData.summary || {};
  const filtersApplied = reportData.filters_applied || {};
  const selectedProject = projects.find(
    p => String(p.id) === String(projectId),
  );
  const selectedEmployee = employees.find(
    e => String(e.id) === String(employeeId),
  );
  const selectedContractor = contractors.find(
    c => String(c.id) === String(contractorId),
  );

  return (
    <div style={{ padding: "20px", direction: dir }}>
      <div
        style={{
          backgroundColor: "#1b3350",
          borderRadius: "12px",
          padding: "24px",
          border: "1px solid #333",
        }}
      >
        <h2
          style={{
            color: "white",
            marginBottom: "20px",
            textAlign: headerAlign,
          }}
        >
          {t("projectReports.tasks", { defaultValue: "تقارير المهام" })}
        </h2>

        <form
          onSubmit={handleSubmit}
          style={{
            backgroundColor: "#1a1f2e",
            padding: "20px",
            borderRadius: "8px",
            marginBottom: "20px",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: "15px",
              marginBottom: "15px",
            }}
          >
            {/* Project Dropdown */}
            <div
              style={{ position: "relative" }}
              className="project-dropdown-container"
            >
              <label
                style={{
                  color: "#ccc",
                  fontSize: "14px",
                  display: "block",
                  marginBottom: "8px",
                  textAlign: headerAlign,
                }}
              >
                {t("projectReports.project", { defaultValue: "المشروع" })}
              </label>
              <div
                onClick={() => {
                  setProjectDropdownOpen(!projectDropdownOpen);
                  if (!projectDropdownOpen) {
                    setTimeout(() => {
                      if (projectSearchInputRef.current) {
                        projectSearchInputRef.current.focus();
                      }
                    }, 100);
                  }
                }}
                style={{
                  width: "100%",
                  padding: "10px",
                  backgroundColor: "#14263b",
                  border: "1px solid #333",
                  borderRadius: "6px",
                  color: "white",
                  fontSize: "16px",
                  cursor: "pointer",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  textAlign: headerAlign,
                  direction: dir,
                }}
              >
                <span>
                  {selectedProject
                    ? getProjectName(selectedProject)
                    : t("projectReports.selectProject", {
                        defaultValue: "اختر المشروع",
                      })}
                </span>
                <span>▼</span>
              </div>
              {projectDropdownOpen && (
                <div
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    backgroundColor: "#14263b",
                    border: "1px solid #333",
                    borderRadius: "6px",
                    marginTop: "4px",
                    maxHeight: "400px",
                    overflowY: "auto",
                    zIndex: 1000,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                  }}
                >
                  <div
                    style={{ padding: "8px", borderBottom: "1px solid #333" }}
                  >
                    <input
                      ref={projectSearchInputRef}
                      type="text"
                      placeholder={t("projectReports.search", {
                        defaultValue: "بحث...",
                      })}
                      value={projectSearchTerm}
                      onChange={e => setProjectSearchTerm(e.target.value)}
                      onClick={e => e.stopPropagation()}
                      style={{
                        width: "100%",
                        padding: "8px",
                        backgroundColor: "#0d1117",
                        border: "none",
                        borderBottom: "1px solid #333",
                        color: "white",
                        borderRadius: "6px 6px 0 0",
                        textAlign: headerAlign,
                        direction: dir,
                      }}
                    />
                  </div>
                  <div
                    style={{
                      padding: "8px",
                      borderBottom: "1px solid #333",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    <button
                      onClick={e => {
                        e.stopPropagation();
                        setProjectPage(prev => Math.max(1, prev - 1));
                      }}
                      disabled={projectPage === 1}
                      style={{
                        padding: "5px 10px",
                        backgroundColor: projectPage === 1 ? "#666" : "#AC2000",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: projectPage === 1 ? "not-allowed" : "pointer",
                        fontSize: "12px",
                      }}
                    >
                      ‹
                    </button>
                    <span
                      style={{
                        color: "#ccc",
                        fontSize: "12px",
                        flex: 1,
                        textAlign: "center",
                      }}
                    >
                      {t("projectReports.page", { defaultValue: "صفحة" })}{" "}
                      {projectPage}
                    </span>
                    <button
                      onClick={e => {
                        e.stopPropagation();
                        setProjectPage(prev => prev + 1);
                      }}
                      disabled={!projectsHasNext}
                      style={{
                        padding: "5px 10px",
                        backgroundColor: projectsHasNext ? "#AC2000" : "#666",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: projectsHasNext ? "pointer" : "not-allowed",
                        fontSize: "12px",
                      }}
                    >
                      ›
                    </button>
                  </div>
                  {filteredProjects.length === 0 ? (
                    <div
                      style={{
                        padding: "20px",
                        textAlign: "center",
                        color: "#ccc",
                      }}
                    >
                      {t("projectReports.noData", {
                        defaultValue: "لا توجد بيانات",
                      })}
                    </div>
                  ) : (
                    <>
                      {filteredProjects.map(project => (
                        <div
                          key={project.id}
                          onClick={() => {
                            setProjectId(String(project.id));
                            setProjectDropdownOpen(false);
                            setProjectSearchTerm("");
                          }}
                          style={{
                            padding: "10px",
                            cursor: "pointer",
                            borderBottom: "1px solid #333",
                            color: "white",
                            backgroundColor:
                              String(projectId) === String(project.id)
                                ? "#2a2a2a"
                                : "transparent",
                            textAlign: headerAlign,
                            direction: dir,
                          }}
                        >
                          {project.code ? `${project.code} - ` : ""}
                          {getProjectName(project)}
                        </div>
                      ))}
                      <div style={{ padding: "10px" }}></div>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Employee Dropdown */}
            <div
              style={{ position: "relative" }}
              className="employee-dropdown-container"
            >
              <label
                style={{
                  color: "#ccc",
                  fontSize: "14px",
                  display: "block",
                  marginBottom: "8px",
                  textAlign: headerAlign,
                }}
              >
                {t("projectReports.employee", { defaultValue: "الموظف" })}
              </label>
              <div
                onClick={() => {
                  setEmployeeDropdownOpen(!employeeDropdownOpen);
                  if (!employeeDropdownOpen) {
                    setTimeout(() => {
                      if (employeeSearchInputRef.current) {
                        employeeSearchInputRef.current.focus();
                      }
                    }, 100);
                  }
                }}
                style={{
                  width: "100%",
                  padding: "10px",
                  backgroundColor: "#14263b",
                  border: "1px solid #333",
                  borderRadius: "6px",
                  color: "white",
                  fontSize: "16px",
                  cursor: "pointer",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  textAlign: headerAlign,
                  direction: dir,
                }}
              >
                <span>
                  {selectedEmployee
                    ? getEmployeeName(selectedEmployee)
                    : t("projectReports.selectEmployee", {
                        defaultValue: "اختر الموظف",
                      })}
                </span>
                <span>▼</span>
              </div>
              {employeeDropdownOpen && (
                <div
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    backgroundColor: "#14263b",
                    border: "1px solid #333",
                    borderRadius: "6px",
                    marginTop: "4px",
                    maxHeight: "400px",
                    overflowY: "auto",
                    zIndex: 1000,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                  }}
                >
                  <div
                    style={{ padding: "8px", borderBottom: "1px solid #333" }}
                  >
                    <input
                      ref={employeeSearchInputRef}
                      type="text"
                      placeholder={t("common.search", {
                        defaultValue: isRTL ? "بحث..." : "Search...",
                      })}
                      value={employeeSearchTerm}
                      onChange={e => setEmployeeSearchTerm(e.target.value)}
                      onClick={e => e.stopPropagation()}
                      style={{
                        width: "100%",
                        padding: "8px",
                        backgroundColor: "#0d1117",
                        border: "none",
                        borderBottom: "1px solid #333",
                        color: "white",
                        borderRadius: "6px 6px 0 0",
                        textAlign: headerAlign,
                        direction: dir,
                      }}
                    />
                  </div>
                  <div
                    style={{
                      padding: "8px",
                      borderBottom: "1px solid #333",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    <button
                      onClick={e => {
                        e.stopPropagation();
                        setEmployeePage(prev => Math.max(1, prev - 1));
                      }}
                      disabled={employeePage === 1}
                      style={{
                        padding: "5px 10px",
                        backgroundColor:
                          employeePage === 1 ? "#666" : "#AC2000",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: employeePage === 1 ? "not-allowed" : "pointer",
                        fontSize: "12px",
                      }}
                    >
                      ‹
                    </button>
                    <span
                      style={{
                        color: "#ccc",
                        fontSize: "12px",
                        flex: 1,
                        textAlign: "center",
                      }}
                    >
                      {t("projectReports.page", { defaultValue: "صفحة" })}{" "}
                      {employeePage}
                    </span>
                    <button
                      onClick={e => {
                        e.stopPropagation();
                        setEmployeePage(prev => prev + 1);
                      }}
                      disabled={!employeesHasNext}
                      style={{
                        padding: "5px 10px",
                        backgroundColor: employeesHasNext ? "#AC2000" : "#666",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: employeesHasNext ? "pointer" : "not-allowed",
                        fontSize: "12px",
                      }}
                    >
                      ›
                    </button>
                  </div>
                  {filteredEmployees.length === 0 ? (
                    <div
                      style={{
                        padding: "20px",
                        textAlign: "center",
                        color: "#ccc",
                      }}
                    >
                      {t("projectReports.noData", {
                        defaultValue: "لا توجد بيانات",
                      })}
                    </div>
                  ) : (
                    <>
                      {filteredEmployees.map(emp => (
                        <div
                          key={emp.id}
                          onClick={() => {
                            setEmployeeId(String(emp.id));
                            setEmployeeDropdownOpen(false);
                            setEmployeeSearchTerm("");
                          }}
                          style={{
                            padding: "10px",
                            cursor: "pointer",
                            borderBottom: "1px solid #333",
                            color: "white",
                            backgroundColor:
                              String(employeeId) === String(emp.id)
                                ? "#2a2a2a"
                                : "transparent",
                            textAlign: headerAlign,
                            direction: dir,
                          }}
                        >
                          {emp.code ? `${emp.code} - ` : ""}
                          {getEmployeeName(emp)}
                        </div>
                      ))}
                      <div style={{ padding: "10px" }}></div>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Contractor Dropdown */}
            <div
              style={{ position: "relative" }}
              className="contractor-dropdown-container"
            >
              <label
                style={{
                  color: "#ccc",
                  fontSize: "14px",
                  display: "block",
                  marginBottom: "8px",
                  textAlign: headerAlign,
                }}
              >
                {t("projectReports.contractor", { defaultValue: "المقاول" })}
              </label>
              <div
                onClick={() => {
                  setContractorDropdownOpen(!contractorDropdownOpen);
                  if (!contractorDropdownOpen) {
                    setTimeout(() => {
                      if (contractorSearchInputRef.current) {
                        contractorSearchInputRef.current.focus();
                      }
                    }, 100);
                  }
                }}
                style={{
                  width: "100%",
                  padding: "10px",
                  backgroundColor: "#14263b",
                  border: "1px solid #333",
                  borderRadius: "6px",
                  color: "white",
                  fontSize: "16px",
                  cursor: "pointer",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  textAlign: headerAlign,
                  direction: dir,
                }}
              >
                <span>
                  {selectedContractor
                    ? getContractorName(selectedContractor)
                    : t("projectReports.selectContractor", {
                        defaultValue: "اختر المقاول",
                      })}
                </span>
                <span>▼</span>
              </div>
              {contractorDropdownOpen && (
                <div
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    backgroundColor: "#14263b",
                    border: "1px solid #333",
                    borderRadius: "6px",
                    marginTop: "4px",
                    maxHeight: "400px",
                    overflowY: "auto",
                    zIndex: 1000,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                  }}
                >
                  <div
                    style={{ padding: "8px", borderBottom: "1px solid #333" }}
                  >
                    <input
                      ref={contractorSearchInputRef}
                      type="text"
                      placeholder={t("common.search", {
                        defaultValue: isRTL ? "بحث..." : "Search...",
                      })}
                      value={contractorSearchTerm}
                      onChange={e => setContractorSearchTerm(e.target.value)}
                      onClick={e => e.stopPropagation()}
                      style={{
                        width: "100%",
                        padding: "8px",
                        backgroundColor: "#0d1117",
                        border: "none",
                        borderBottom: "1px solid #333",
                        color: "white",
                        borderRadius: "6px 6px 0 0",
                        textAlign: headerAlign,
                        direction: dir,
                      }}
                    />
                  </div>
                  <div
                    style={{
                      padding: "8px",
                      borderBottom: "1px solid #333",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    <button
                      onClick={e => {
                        e.stopPropagation();
                        setContractorPage(prev => Math.max(1, prev - 1));
                      }}
                      disabled={contractorPage === 1}
                      style={{
                        padding: "5px 10px",
                        backgroundColor:
                          contractorPage === 1 ? "#666" : "#AC2000",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor:
                          contractorPage === 1 ? "not-allowed" : "pointer",
                        fontSize: "12px",
                      }}
                    >
                      ‹
                    </button>
                    <span
                      style={{
                        color: "#ccc",
                        fontSize: "12px",
                        flex: 1,
                        textAlign: "center",
                      }}
                    >
                      {t("projectReports.page", { defaultValue: "صفحة" })}{" "}
                      {contractorPage}
                    </span>
                    <button
                      onClick={e => {
                        e.stopPropagation();
                        setContractorPage(prev => prev + 1);
                      }}
                      disabled={!contractorsHasNext}
                      style={{
                        padding: "5px 10px",
                        backgroundColor: contractorsHasNext
                          ? "#AC2000"
                          : "#666",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: contractorsHasNext ? "pointer" : "not-allowed",
                        fontSize: "12px",
                      }}
                    >
                      ›
                    </button>
                  </div>
                  {filteredContractors.length === 0 ? (
                    <div
                      style={{
                        padding: "20px",
                        textAlign: "center",
                        color: "#ccc",
                      }}
                    >
                      {t("projectReports.noData", {
                        defaultValue: "لا توجد بيانات",
                      })}
                    </div>
                  ) : (
                    <>
                      {filteredContractors.map(con => (
                        <div
                          key={con.id}
                          onClick={() => {
                            setContractorId(String(con.id));
                            setContractorDropdownOpen(false);
                            setContractorSearchTerm("");
                          }}
                          style={{
                            padding: "10px",
                            cursor: "pointer",
                            borderBottom: "1px solid #333",
                            color: "white",
                            backgroundColor:
                              String(contractorId) === String(con.id)
                                ? "#2a2a2a"
                                : "transparent",
                            textAlign: headerAlign,
                            direction: dir,
                          }}
                        >
                          {con.code ? `${con.code} - ` : ""}
                          {getContractorName(con)}
                        </div>
                      ))}
                      <div style={{ padding: "10px" }}></div>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Status */}
            <div
              style={{ position: "relative" }}
              className="status-dropdown-container"
            >
              <label
                style={{
                  color: "#ccc",
                  fontSize: "14px",
                  display: "block",
                  marginBottom: "8px",
                  textAlign: headerAlign,
                }}
              >
                {t("projectReports.status", { defaultValue: "الحالة" })}
              </label>
              <div
                onClick={() => setStatusDropdownOpen(!statusDropdownOpen)}
                style={{
                  width: "100%",
                  padding: "10px",
                  backgroundColor: "#14263b",
                  border: "1px solid #333",
                  borderRadius: "6px",
                  color: "white",
                  fontSize: "16px",
                  cursor: "pointer",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  textAlign: headerAlign,
                  direction: dir,
                }}
              >
                <span>
                  {status
                    ? status === "pending"
                      ? t("projectReports.pending", {
                          defaultValue: "قيد الانتظار",
                        })
                      : status === "in_progress"
                        ? t("projectReports.inProgress", {
                            defaultValue: "قيد التنفيذ",
                          })
                        : status === "completed"
                          ? t("projectReports.completed", {
                              defaultValue: "مكتمل",
                            })
                          : status === "on_hold"
                            ? t("projectReports.onHold", {
                                defaultValue: "معلق",
                              })
                            : status
                    : t("projectReports.selectStatus", {
                        defaultValue: "اختر الحالة",
                      })}
                </span>
                <span>▼</span>
              </div>
              {statusDropdownOpen && (
                <div
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    backgroundColor: "#14263b",
                    border: "1px solid #333",
                    borderRadius: "6px",
                    marginTop: "4px",
                    maxHeight: "300px",
                    overflowY: "auto",
                    zIndex: 1000,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                  }}
                >
                  {[
                    {
                      value: "pending",
                      label: t("projectReports.pending", {
                        defaultValue: "قيد الانتظار",
                      }),
                    },
                    {
                      value: "in_progress",
                      label: t("projectReports.inProgress", {
                        defaultValue: "قيد التنفيذ",
                      }),
                    },
                    {
                      value: "completed",
                      label: t("projectReports.completed", {
                        defaultValue: "مكتمل",
                      }),
                    },
                    {
                      value: "on_hold",
                      label: t("projectReports.onHold", {
                        defaultValue: "معلق",
                      }),
                    },
                  ].map(option => (
                    <div
                      key={option.value}
                      onClick={() => {
                        setStatus(option.value);
                        setStatusDropdownOpen(false);
                      }}
                      style={{
                        padding: "10px",
                        cursor: "pointer",
                        borderBottom: "1px solid #333",
                        color: "white",
                        backgroundColor:
                          status === option.value ? "#2a2a2a" : "transparent",
                        textAlign: headerAlign,
                        direction: dir,
                      }}
                    >
                      {option.label}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Priority */}
            <div
              style={{ position: "relative" }}
              className="priority-dropdown-container"
            >
              <label
                style={{
                  color: "#ccc",
                  fontSize: "14px",
                  display: "block",
                  marginBottom: "8px",
                  textAlign: headerAlign,
                }}
              >
                {t("projectReports.priority", { defaultValue: "الأولوية" })}
              </label>
              <div
                onClick={() => setPriorityDropdownOpen(!priorityDropdownOpen)}
                style={{
                  width: "100%",
                  padding: "10px",
                  backgroundColor: "#14263b",
                  border: "1px solid #333",
                  borderRadius: "6px",
                  color: "white",
                  fontSize: "16px",
                  cursor: "pointer",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  textAlign: headerAlign,
                  direction: dir,
                }}
              >
                <span>
                  {priority
                    ? priority === "low"
                      ? t("projectReports.low", { defaultValue: "منخفضة" })
                      : priority === "medium"
                        ? t("projectReports.medium", { defaultValue: "متوسطة" })
                        : priority === "high"
                          ? t("projectReports.high", { defaultValue: "عالية" })
                          : priority === "urgent"
                            ? t("projectReports.urgent", {
                                defaultValue: "عاجلة",
                              })
                            : priority
                    : t("projectReports.selectPriority", {
                        defaultValue: "اختر الأولوية",
                      })}
                </span>
                <span>▼</span>
              </div>
              {priorityDropdownOpen && (
                <div
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    backgroundColor: "#14263b",
                    border: "1px solid #333",
                    borderRadius: "6px",
                    marginTop: "4px",
                    maxHeight: "300px",
                    overflowY: "auto",
                    zIndex: 1000,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                  }}
                >
                  {[
                    {
                      value: "low",
                      label: t("projectReports.low", {
                        defaultValue: "منخفضة",
                      }),
                    },
                    {
                      value: "medium",
                      label: t("projectReports.medium", {
                        defaultValue: "متوسطة",
                      }),
                    },
                    {
                      value: "high",
                      label: t("projectReports.high", {
                        defaultValue: "عالية",
                      }),
                    },
                    {
                      value: "urgent",
                      label: t("projectReports.urgent", {
                        defaultValue: "عاجلة",
                      }),
                    },
                  ].map(option => (
                    <div
                      key={option.value}
                      onClick={() => {
                        setPriority(option.value);
                        setPriorityDropdownOpen(false);
                      }}
                      style={{
                        padding: "10px",
                        cursor: "pointer",
                        borderBottom: "1px solid #333",
                        color: "white",
                        backgroundColor:
                          priority === option.value ? "#2a2a2a" : "transparent",
                        textAlign: headerAlign,
                        direction: dir,
                      }}
                    >
                      {option.label}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* From Date */}
            <div>
              <label
                style={{
                  color: "#ccc",
                  fontSize: "14px",
                  display: "block",
                  marginBottom: "8px",
                  textAlign: headerAlign,
                }}
              >
                {t("projectReports.fromDate", { defaultValue: "من تاريخ" })}
              </label>
              <input
                type="date"
                value={fromDate}
                onChange={e => setFromDate(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px",
                  backgroundColor: "#14263b",
                  border: "1px solid #333",
                  borderRadius: "6px",
                  color: "white",
                  fontSize: "16px",
                }}
              />
            </div>

            {/* To Date */}
            <div>
              <label
                style={{
                  color: "#ccc",
                  fontSize: "14px",
                  display: "block",
                  marginBottom: "8px",
                  textAlign: headerAlign,
                }}
              >
                {t("projectReports.toDate", { defaultValue: "إلى تاريخ" })}
              </label>
              <input
                type="date"
                value={toDate}
                onChange={e => setToDate(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px",
                  backgroundColor: "#14263b",
                  border: "1px solid #333",
                  borderRadius: "6px",
                  color: "white",
                  fontSize: "16px",
                }}
              />
            </div>

            {/* Sort By */}
            <div>
              <label
                style={{
                  color: "#ccc",
                  fontSize: "14px",
                  display: "block",
                  marginBottom: "8px",
                  textAlign: headerAlign,
                }}
              >
                {t("projectReports.sortBy", { defaultValue: "ترتيب حسب" })}
              </label>
              <select
                value={sortBy}
                onChange={e => setSortBy(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px",
                  backgroundColor: "#14263b",
                  border: "1px solid #333",
                  borderRadius: "6px",
                  color: "white",
                  fontSize: "16px",
                }}
              >
                <option value="created_at">
                  {t("projectReports.createdAt", {
                    defaultValue: "تاريخ الإنشاء",
                  })}
                </option>
                <option value="start_date">
                  {t("projectReports.startDate", {
                    defaultValue: "Start Date",
                  })}
                </option>
                <option value="end_date">
                  {t("projectReports.endDate", { defaultValue: "End Date" })}
                </option>
                <option value="priority">
                  {t("projectReports.priority", { defaultValue: "الأولوية" })}
                </option>
              </select>
            </div>

            {/* Sort Order */}
            <div>
              <label
                style={{
                  color: "#ccc",
                  fontSize: "14px",
                  display: "block",
                  marginBottom: "8px",
                  textAlign: headerAlign,
                }}
              >
                {t("projectReports.sortOrder", { defaultValue: "ترتيب" })}
              </label>
              <select
                value={sortOrder}
                onChange={e => setSortOrder(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px",
                  backgroundColor: "#14263b",
                  border: "1px solid #333",
                  borderRadius: "6px",
                  color: "white",
                  fontSize: "16px",
                }}
              >
                <option value="asc">
                  {t("projectReports.asc", { defaultValue: "تصاعدي" })}
                </option>
                <option value="desc">
                  {t("projectReports.desc", { defaultValue: "تنازلي" })}
                </option>
              </select>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginTop: "15px",
            }}
          >
            <button
              type="submit"
              disabled={isLoading}
              style={{
                backgroundColor: isLoading ? "#666" : "#AC2000",
                color: "white",
                border: "none",
                padding: "12px 24px",
                borderRadius: "8px",
                cursor: isLoading ? "not-allowed" : "pointer",
                fontSize: "16px",
                opacity: isLoading ? 0.6 : 1,
              }}
            >
              {isLoading
                ? t("projectReports.loading", {
                    defaultValue: "جاري التحميل...",
                  })
                : t("projectReports.viewReport", {
                    defaultValue: "عرض التقرير",
                  })}
            </button>
          </div>
        </form>

        {/* Action Buttons */}
        {tasksReport && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "10px",
              marginBottom: "20px",
              flexWrap: "wrap",
            }}
          >
            <button
              onClick={handlePreviewAll}
              style={{
                backgroundColor: "#444",
                color: "white",
                border: "none",
                padding: "12px 20px",
                borderRadius: 8,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 8,
                justifyContent: "center",
              }}
            >
              <FaEye />{" "}
              {t("projectReports.previewAllPDF", {
                defaultValue: "عرض PDF للكل",
              })}
            </button>
            <button
              onClick={handlePrintAll}
              disabled={isGeneratingPdf}
              style={{
                backgroundColor: isGeneratingPdf ? "#666" : "#17a2b8",
                color: "white",
                border: "none",
                padding: "12px 20px",
                borderRadius: 8,
                cursor: isGeneratingPdf ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                gap: 8,
                justifyContent: "center",
                opacity: isGeneratingPdf ? 0.6 : 1,
              }}
            >
              <FaPrint />{" "}
              {isGeneratingPdf
                ? t("projectReports.generating", {
                    defaultValue: "جاري التوليد...",
                  })
                : t("projectReports.printAllPDF", {
                    defaultValue: "طباعة PDF للكل",
                  })}
            </button>
            <button
              onClick={handleExportAllToExcel}
              style={{
                backgroundColor: "#28a745",
                color: "white",
                border: "none",
                padding: "12px 20px",
                borderRadius: 8,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 8,
                justifyContent: "center",
              }}
            >
              <FaFileExcel />{" "}
              {t("projectReports.exportAllExcel", {
                defaultValue: "تنزيل Excel للكل",
              })}
            </button>
          </div>
        )}

        {/* Report Data */}
        {tasksReport && (
          <div
            style={{
              backgroundColor: "#1a1f2e",
              padding: "20px",
              borderRadius: "8px",
              marginTop: "20px",
            }}
          >
            <div
              style={{
                color: "#ccc",
                marginBottom: "20px",
                textAlign: headerAlign,
                direction: dir,
              }}
            >
              <div style={{ marginBottom: "8px" }}>
                <strong>
                  {t("projectReports.period", { defaultValue: "Period" })}:
                </strong>{" "}
                {formatDate(fromDate)} - {formatDate(toDate)}
              </div>
            </div>

            {/* Summary Section */}
            {summary && Object.keys(summary).length > 0 && (
              <div
                style={{
                  backgroundColor: "#1b3350",
                  padding: "20px",
                  borderRadius: "8px",
                  marginBottom: "20px",
                  border: "1px solid #333",
                  textAlign: headerAlign,
                  direction: dir,
                }}
              >
                <h3
                  style={{
                    color: "white",
                    marginBottom: "15px",
                    fontSize: "18px",
                    textAlign: headerAlign,
                  }}
                >
                  {t("projectReports.summary", {
                    defaultValue: "Report Summary",
                  })}
                </h3>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                    gap: "15px",
                  }}
                >
                  {summary.total_count !== undefined && (
                    <div
                      style={{
                        backgroundColor: "#1a1f2e",
                        padding: "15px",
                        borderRadius: "6px",
                        border: "1px solid #333",
                        textAlign: headerAlign,
                      }}
                    >
                      <div
                        style={{
                          color: "#9ca3af",
                          fontSize: "14px",
                          marginBottom: "5px",
                        }}
                      >
                        {t("projectReports.totalCount", {
                          defaultValue: "Total Tasks",
                        })}
                      </div>
                      <div
                        style={{
                          color: "white",
                          fontSize: "20px",
                          fontWeight: "bold",
                        }}
                      >
                        {summary.total_count || 0}
                      </div>
                    </div>
                  )}
                  {summary.average_progress !== undefined && (
                    <div
                      style={{
                        backgroundColor: "#1a1f2e",
                        padding: "15px",
                        borderRadius: "6px",
                        border: "1px solid #333",
                        textAlign: headerAlign,
                      }}
                    >
                      <div
                        style={{
                          color: "#9ca3af",
                          fontSize: "14px",
                          marginBottom: "5px",
                        }}
                      >
                        {t("projectReports.averageProgress", {
                          defaultValue: "Average Progress",
                        })}
                      </div>
                      <div
                        style={{
                          color: "#17a2b8",
                          fontSize: "20px",
                          fontWeight: "bold",
                        }}
                      >
                        {summary.average_progress || 0}%
                      </div>
                    </div>
                  )}
                </div>

                {/* By Status Summary */}
                {summary.by_status &&
                  Object.keys(summary.by_status).length > 0 && (
                    <div style={{ marginTop: "20px" }}>
                      <h4
                        style={{
                          color: "white",
                          marginBottom: "10px",
                          fontSize: "16px",
                          textAlign: headerAlign,
                        }}
                      >
                        {t("projectReports.byStatus", {
                          defaultValue: "By Status",
                        })}
                      </h4>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            "repeat(auto-fit, minmax(180px, 1fr))",
                          gap: "10px",
                        }}
                      >
                        {Object.entries(summary.by_status).map(
                          ([status, statusData]) => (
                            <div
                              key={status}
                              style={{
                                backgroundColor: "#1a1f2e",
                                padding: "12px",
                                borderRadius: "6px",
                                border: "1px solid #333",
                                textAlign: headerAlign,
                              }}
                            >
                              <div
                                style={{
                                  color: "#9ca3af",
                                  fontSize: "12px",
                                  marginBottom: "5px",
                                  textTransform: "capitalize",
                                }}
                              >
                                {translateTaskStatus(status)}
                              </div>
                              <div
                                style={{
                                  color: "white",
                                  fontSize: "14px",
                                  marginBottom: "3px",
                                }}
                              >
                                {t("projectReports.count", {
                                  defaultValue: "Count",
                                })}
                                : <strong>{statusData.count || 0}</strong>
                              </div>
                              {statusData.avg_progress !== undefined && (
                                <div
                                  style={{ color: "#17a2b8", fontSize: "14px" }}
                                >
                                  {t("projectReports.avgProgress", {
                                    defaultValue: "Avg. Progress",
                                  })}
                                  :{" "}
                                  <strong>
                                    {statusData.avg_progress || 0}%
                                  </strong>
                                </div>
                              )}
                            </div>
                          ),
                        )}
                      </div>
                    </div>
                  )}

                {/* By Priority Summary */}
                {summary.by_priority &&
                  Object.keys(summary.by_priority).length > 0 && (
                    <div style={{ marginTop: "20px" }}>
                      <h4
                        style={{
                          color: "white",
                          marginBottom: "10px",
                          fontSize: "16px",
                          textAlign: headerAlign,
                        }}
                      >
                        {t("projectReports.byPriority", {
                          defaultValue: "By Priority",
                        })}
                      </h4>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            "repeat(auto-fit, minmax(180px, 1fr))",
                          gap: "10px",
                        }}
                      >
                        {Object.entries(summary.by_priority).map(
                          ([priority, priorityData]) => (
                            <div
                              key={priority}
                              style={{
                                backgroundColor: "#1a1f2e",
                                padding: "12px",
                                borderRadius: "6px",
                                border: "1px solid #333",
                                textAlign: headerAlign,
                              }}
                            >
                              <div
                                style={{
                                  color: "#9ca3af",
                                  fontSize: "12px",
                                  marginBottom: "5px",
                                  textTransform: "capitalize",
                                }}
                              >
                                {translateTaskPriority(priority)}
                              </div>
                              <div style={{ color: "white", fontSize: "14px" }}>
                                {t("projectReports.count", {
                                  defaultValue: "Count",
                                })}
                                : <strong>{priorityData.count || 0}</strong>
                              </div>
                            </div>
                          ),
                        )}
                      </div>
                    </div>
                  )}
              </div>
            )}

            {tasks && tasks.length > 0 ? (
              <div style={{ overflowX: "auto" }}>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    backgroundColor: "#1b3350",
                  }}
                >
                  <thead>
                    <tr style={{ backgroundColor: "#AC2000" }}>
                      <th
                        style={{
                          padding: "12px",
                          textAlign: "center",
                          color: "white",
                          border: "1px solid #333",
                        }}
                      >
                        {t("projectReports.taskNo", {
                          defaultValue: "Task No",
                        })}
                      </th>
                      <th
                        style={{
                          padding: "12px",
                          textAlign: "center",
                          color: "white",
                          border: "1px solid #333",
                        }}
                      >
                        {t("projectReports.taskName", {
                          defaultValue: "Task Name",
                        })}
                      </th>
                      <th
                        style={{
                          padding: "12px",
                          textAlign: "center",
                          color: "white",
                          border: "1px solid #333",
                        }}
                      >
                        {t("projectReports.project", {
                          defaultValue: "المشروع",
                        })}
                      </th>
                      <th
                        style={{
                          padding: "12px",
                          textAlign: "center",
                          color: "white",
                          border: "1px solid #333",
                        }}
                      >
                        {t("projectReports.employee", {
                          defaultValue: "الموظف",
                        })}
                      </th>
                      <th
                        style={{
                          padding: "12px",
                          textAlign: "center",
                          color: "white",
                          border: "1px solid #333",
                        }}
                      >
                        {t("projectReports.contractor", {
                          defaultValue: "المقاول",
                        })}
                      </th>
                      <th
                        style={{
                          padding: "12px",
                          textAlign: "center",
                          color: "white",
                          border: "1px solid #333",
                        }}
                      >
                        {t("projectReports.status", { defaultValue: "الحالة" })}
                      </th>
                      <th
                        style={{
                          padding: "12px",
                          textAlign: "center",
                          color: "white",
                          border: "1px solid #333",
                        }}
                      >
                        {t("projectReports.priority", {
                          defaultValue: "الأولوية",
                        })}
                      </th>
                      <th
                        style={{
                          padding: "12px",
                          textAlign: "center",
                          color: "white",
                          border: "1px solid #333",
                        }}
                      >
                        {t("projectReports.startDate", {
                          defaultValue: "Start Date",
                        })}
                      </th>
                      <th
                        style={{
                          padding: "12px",
                          textAlign: "center",
                          color: "white",
                          border: "1px solid #333",
                        }}
                      >
                        {t("projectReports.endDate", {
                          defaultValue: "End Date",
                        })}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {tasks.map((task, idx) => (
                      <tr
                        key={task.id || idx}
                        style={{
                          backgroundColor:
                            idx % 2 === 0 ? "#1b3350" : "#1a1f2e",
                        }}
                      >
                        <td
                          style={{
                            padding: "12px",
                            textAlign: "center",
                            color: "white",
                            border: "1px solid #333",
                          }}
                        >
                          {task.id || "—"}
                        </td>
                        <td
                          style={{
                            padding: "12px",
                            textAlign: isRTL ? "right" : "left",
                            color: "white",
                            border: "1px solid #333",
                          }}
                        >
                          {isRTL
                            ? task.name_ar || task.name_en
                            : task.name_en || task.name_ar || "—"}
                        </td>
                        <td
                          style={{
                            padding: "12px",
                            textAlign: isRTL ? "right" : "left",
                            color: "white",
                            border: "1px solid #333",
                          }}
                        >
                          {isRTL
                            ? task.project?.name_ar || task.project?.name_en
                            : task.project?.name_en ||
                              task.project?.name_ar ||
                              "—"}
                        </td>
                        <td
                          style={{
                            padding: "12px",
                            textAlign: isRTL ? "right" : "left",
                            color: "white",
                            border: "1px solid #333",
                          }}
                        >
                          {isRTL
                            ? task.employee?.name_ar || task.employee?.name_en
                            : task.employee?.name_en ||
                              task.employee?.name_ar ||
                              "—"}
                        </td>
                        <td
                          style={{
                            padding: "12px",
                            textAlign: isRTL ? "right" : "left",
                            color: "white",
                            border: "1px solid #333",
                          }}
                        >
                          {isRTL
                            ? task.contractor?.name_ar ||
                              task.contractor?.name_en
                            : task.contractor?.name_en ||
                              task.contractor?.name_ar ||
                              "—"}
                        </td>
                        <td
                          style={{
                            padding: "12px",
                            textAlign: "center",
                            color: "white",
                            border: "1px solid #333",
                          }}
                        >
                          {translateTaskStatus(task.status) || "—"}
                        </td>
                        <td
                          style={{
                            padding: "12px",
                            textAlign: "center",
                            color: "white",
                            border: "1px solid #333",
                          }}
                        >
                          {translateTaskPriority(task.priority) || "—"}
                        </td>
                        <td
                          style={{
                            padding: "12px",
                            textAlign: "center",
                            color: "white",
                            border: "1px solid #333",
                          }}
                        >
                          {formatDate(task.start_date)}
                        </td>
                        <td
                          style={{
                            padding: "12px",
                            textAlign: "center",
                            color: "white",
                            border: "1px solid #333",
                          }}
                        >
                          {formatDate(task.end_date)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div
                style={{
                  padding: "30px",
                  textAlign: "center",
                  color: "#9ca3af",
                }}
              >
                {t("projectReports.noData", { defaultValue: "لا توجد بيانات" })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Hidden print template */}
      {pdfType === "all" &&
        pdfChunks &&
        pdfChunks.length > 0 &&
        pdfChunks.map((chunk, chunkIndex) => (
          <div
            key={chunkIndex}
            id={`tasks-report-pdf-all-${chunkIndex}`}
            aria-hidden="true"
            style={{
              position: "absolute",
              left: "-9999px",
              top: 0,
              width: "794px",
              height: "1123px",
              backgroundColor: "#ffffff",
              color: "#000000",
              fontFamily:
                '"Times New Roman Bold", "Times New Roman", "Times", serif',
              fontWeight: "bold",
              padding: "40px",
              boxSizing: "border-box",
              overflow: "visible",
            }}
          >
            <div
              style={{
                border: "5px solid #000",
                padding: "3px",
                backgroundColor: "#ffffff",
                boxSizing: "border-box",
                width: "100%",
                height: "1043px",
                overflow: "visible",
              }}
            >
              <div
                style={{
                  border: "2px solid #000",
                  minHeight: "1033px",
                  height: "1033px",
                  padding: "18px 18px 10px",
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                  fontFamily:
                    '"Times New Roman Bold", "Times New Roman", "Times", serif',
                  fontWeight: "bold",
                  WebkitFontSmoothing: "antialiased",
                  MozOsxFontSmoothing: "grayscale",
                  textRendering: "optimizeLegibility",
                  direction: dir,
                  backgroundColor: "#ffffff",
                  boxSizing: "border-box",
                  width: "100%",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    backgroundImage: `url(${headerLogo})`,
                    backgroundSize: "95%",
                    backgroundPosition: "center center",
                    backgroundRepeat: "no-repeat",
                    opacity: 0.08,
                    zIndex: 0,
                    pointerEvents: "none",
                  }}
                />
                <div
                  style={{
                    position: "relative",
                    zIndex: 1,
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    minHeight: "100%",
                  }}
                >
                  <img
                    src={`${publicUrl}/HeaderAce.png`}
                    alt="Header"
                    style={{
                      width: "100%",
                      height: "auto",
                      objectFit: "contain",
                      marginBottom: "8px",
                      borderBottom: "4px double #000",
                      paddingBottom: "8px",
                      flexShrink: 0,
                    }}
                    crossOrigin="anonymous"
                  />

                  <div
                    style={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      overflow: "hidden",
                      minHeight: 0,
                      paddingBottom: "120px",
                    }}
                  >
                    <div
                      style={{
                        marginTop: "12px",
                        textAlign: "center",
                        marginBottom: "20px",
                        flexShrink: 0,
                      }}
                    >
                      <h2
                        style={{
                          fontSize: "20px",
                          fontWeight: "700",
                          color: "#000080",
                          margin: 0,
                        }}
                      >
                        {t("projectReports.tasks", {
                          defaultValue: "تقارير المهام",
                        })}
                      </h2>
                      <p
                        style={{
                          fontSize: "12px",
                          margin: "0 0 4px",
                          color: "#333",
                          fontFamily: '"Times New Roman", serif',
                          fontWeight: "normal",
                        }}
                      >
                        {new Date().toLocaleDateString(
                          isRTL ? "ar-SA" : "en-US",
                          { year: "numeric", month: "long", day: "numeric" },
                        )}
                      </p>
                      {(projectId ||
                        employeeId ||
                        contractorId ||
                        status ||
                        priority ||
                        fromDate ||
                        toDate) && (
                        <div
                          style={{
                            fontSize: "11px",
                            color: "#555",
                            direction: isRTL ? "rtl" : "ltr",
                            fontFamily: '"Times New Roman", serif',
                            fontWeight: "normal",
                          }}
                        >
                          {[
                            selectedProject &&
                              `${t("projectReports.project", { defaultValue: "المشروع" })}: ${getProjectName(selectedProject)}`,
                            selectedEmployee &&
                              `${t("projectReports.employee", { defaultValue: "الموظف" })}: ${getEmployeeName(selectedEmployee)}`,
                            selectedContractor &&
                              `${t("projectReports.contractor", { defaultValue: "المقاول" })}: ${getContractorName(selectedContractor)}`,
                            status &&
                              `${t("projectReports.status", { defaultValue: "الحالة" })}: ${translateTaskStatus(status)}`,
                            priority &&
                              `${t("projectReports.priority", { defaultValue: "الأولوية" })}: ${translateTaskPriority(priority)}`,
                            fromDate &&
                              `${t("projectReports.fromDate", { defaultValue: "من" })}: ${formatDate(fromDate)}`,
                            toDate &&
                              `${t("projectReports.toDate", { defaultValue: "إلى" })}: ${formatDate(toDate)}`,
                          ]
                            .filter(Boolean)
                            .join(" | ")}
                        </div>
                      )}
                    </div>
                    <div
                      style={{
                        color: "#000",
                        fontSize: "12px",
                        flex: 1,
                        overflow: "hidden",
                      }}
                    >
                      {chunk && chunk.length > 0 ? (
                        <table
                          style={{
                            width: "100%",
                            borderCollapse: "collapse",
                            border: "1px solid #000",
                            fontSize: "11px",
                          }}
                        >
                          <thead>
                            <tr
                              style={{
                                backgroundColor: "rgba(238, 242, 247, 0.7)",
                                fontWeight: "bold",
                              }}
                            >
                              <th
                                style={{
                                  border: "1px solid #000",
                                  padding: "6px",
                                  textAlign: "center",
                                  width: "36px",
                                }}
                              >
                                #
                              </th>
                              <th
                                style={{
                                  border: "1px solid #000",
                                  padding: "6px",
                                  textAlign: "center",
                                }}
                              >
                                {t("projectReports.taskName", {
                                  defaultValue: "Task Name",
                                })}
                              </th>
                              <th
                                style={{
                                  border: "1px solid #000",
                                  padding: "6px",
                                  textAlign: "center",
                                }}
                              >
                                {t("projectReports.project", {
                                  defaultValue: "المشروع",
                                })}
                              </th>
                              <th
                                style={{
                                  border: "1px solid #000",
                                  padding: "6px",
                                  textAlign: "center",
                                }}
                              >
                                {t("projectReports.employee", {
                                  defaultValue: "الموظف",
                                })}
                              </th>
                              <th
                                style={{
                                  border: "1px solid #000",
                                  padding: "6px",
                                  textAlign: "center",
                                }}
                              >
                                {t("projectReports.contractor", {
                                  defaultValue: "المقاول",
                                })}
                              </th>
                              <th
                                style={{
                                  border: "1px solid #000",
                                  padding: "6px",
                                  textAlign: "center",
                                }}
                              >
                                {t("projectReports.status", {
                                  defaultValue: "الحالة",
                                })}
                              </th>
                              <th
                                style={{
                                  border: "1px solid #000",
                                  padding: "6px",
                                  textAlign: "center",
                                }}
                              >
                                {t("projectReports.priority", {
                                  defaultValue: "الأولوية",
                                })}
                              </th>
                              <th
                                style={{
                                  border: "1px solid #000",
                                  padding: "6px",
                                  textAlign: "center",
                                }}
                              >
                                {t("projectReports.startDate", {
                                  defaultValue: "Start Date",
                                })}
                              </th>
                              <th
                                style={{
                                  border: "1px solid #000",
                                  padding: "6px",
                                  textAlign: "center",
                                }}
                              >
                                {t("projectReports.endDate", {
                                  defaultValue: "End Date",
                                })}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {chunk.slice(0, 18).map((task, idx) => (
                              <tr
                                key={task.id || idx}
                                style={{
                                  backgroundColor:
                                    idx % 2 === 0
                                      ? "rgba(255, 255, 255, 0.7)"
                                      : "rgba(255, 255, 255, 0.5)",
                                }}
                              >
                                <td
                                  style={{
                                    border: "1px solid #000",
                                    padding: "4px",
                                    textAlign: "center",
                                    fontSize: "10px",
                                  }}
                                >
                                  {chunkIndex * 18 + idx + 1}
                                </td>
                                <td
                                  style={{
                                    border: "1px solid #000",
                                    padding: "4px",
                                    textAlign: isRTL ? "right" : "left",
                                    fontSize: "10px",
                                  }}
                                >
                                  {isRTL
                                    ? task.name_ar || task.name_en
                                    : task.name_en || task.name_ar || "—"}
                                </td>
                                <td
                                  style={{
                                    border: "1px solid #000",
                                    padding: "4px",
                                    textAlign: isRTL ? "right" : "left",
                                    fontSize: "10px",
                                  }}
                                >
                                  {isRTL
                                    ? task.project?.name_ar ||
                                      task.project?.name_en
                                    : task.project?.name_en ||
                                      task.project?.name_ar ||
                                      "—"}
                                </td>
                                <td
                                  style={{
                                    border: "1px solid #000",
                                    padding: "4px",
                                    textAlign: isRTL ? "right" : "left",
                                    fontSize: "10px",
                                  }}
                                >
                                  {isRTL
                                    ? task.employee?.name_ar ||
                                      task.employee?.name_en
                                    : task.employee?.name_en ||
                                      task.employee?.name_ar ||
                                      "—"}
                                </td>
                                <td
                                  style={{
                                    border: "1px solid #000",
                                    padding: "4px",
                                    textAlign: isRTL ? "right" : "left",
                                    fontSize: "10px",
                                  }}
                                >
                                  {isRTL
                                    ? task.contractor?.name_ar ||
                                      task.contractor?.name_en
                                    : task.contractor?.name_en ||
                                      task.contractor?.name_ar ||
                                      "—"}
                                </td>
                                <td
                                  style={{
                                    border: "1px solid #000",
                                    padding: "4px",
                                    textAlign: "center",
                                    fontSize: "10px",
                                  }}
                                >
                                  {translateTaskStatus(task.status) || "—"}
                                </td>
                                <td
                                  style={{
                                    border: "1px solid #000",
                                    padding: "4px",
                                    textAlign: "center",
                                    fontSize: "10px",
                                  }}
                                >
                                  {translateTaskPriority(task.priority) || "—"}
                                </td>
                                <td
                                  style={{
                                    border: "1px solid #000",
                                    padding: "4px",
                                    textAlign: "center",
                                    fontSize: "10px",
                                  }}
                                >
                                  {formatDate(task.start_date)}
                                </td>
                                <td
                                  style={{
                                    border: "1px solid #000",
                                    padding: "4px",
                                    textAlign: "center",
                                    fontSize: "10px",
                                  }}
                                >
                                  {formatDate(task.end_date)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : (
                        <div style={{ textAlign: "center", padding: "20px" }}>
                          {t("projectReports.noData", {
                            defaultValue: "لا توجد بيانات",
                          })}
                        </div>
                      )}
                    </div>
                  </div>

                  <div
                    style={{
                      position: "absolute",
                      bottom: "10px",
                      left: "18px",
                      right: "18px",
                      flexShrink: 0,
                    }}
                  >
                    <img
                      src={`${publicUrl}/FooterAce.png`}
                      alt="Footer"
                      style={{
                        width: "100%",
                        height: "auto",
                        objectFit: "contain",
                          borderTop: "4px double #000",
                          paddingTop: "8px",
                      }}
                      crossOrigin="anonymous"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}

      {/* Preview Modal */}
      {isPreviewOpen &&
        tasksReport &&
        createPortal(
          <div
            style={{
              position: "fixed",
              inset: 0,
              backgroundColor: "rgba(0, 0, 0, 0.9)",
              zIndex: 999999,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "flex-start",
              padding: "20px",
              overflow: "auto",
              boxSizing: "border-box",
            }}
            onClick={e => {
              if (e.target === e.currentTarget) {
                setIsPreviewOpen(false);
              }
            }}
          >
            <div
              style={{
                position: "absolute",
                top: "20px",
                right: "20px",
                display: "flex",
                gap: "10px",
                zIndex: 1000000,
              }}
            >
              <button
                onClick={() => setPreviewZoom(prev => Math.min(2, prev + 0.1))}
                style={{
                  backgroundColor: "#333",
                  color: "white",
                  border: "none",
                  padding: "10px 15px",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "14px",
                }}
              >
                +
              </button>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: "50px",
                  padding: "8px 12px",
                  backgroundColor: "rgba(0, 0, 0, 0.5)",
                  borderRadius: "6px",
                  color: "white",
                  fontSize: "14px",
                  textAlign: "center",
                }}
              >
                {Math.round(previewZoom * 100)}%
              </div>
              <button
                onClick={() =>
                  setPreviewZoom(prev => Math.max(0.1, prev - 0.1))
                }
                style={{
                  backgroundColor: "#333",
                  color: "white",
                  border: "none",
                  padding: "10px 15px",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "14px",
                }}
              >
                −
              </button>
              <button
                onClick={() => {
                  setPreviewZoom(1.0);
                  setPreviewPosition({ x: 0, y: 0 });
                }}
                style={{
                  backgroundColor: "#333",
                  color: "white",
                  border: "none",
                  padding: "10px 15px",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "14px",
                }}
              >
                {t("common.reset", { defaultValue: "إعادة تعيين" })}
              </button>
              <button
                onClick={handlePrintAll}
                disabled={isGeneratingPdf}
                style={{
                  backgroundColor: isGeneratingPdf ? "#666" : "#dc3545",
                  color: "white",
                  border: "none",
                  padding: "10px 15px",
                  borderRadius: "6px",
                  cursor: isGeneratingPdf ? "not-allowed" : "pointer",
                  fontSize: "14px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  opacity: isGeneratingPdf ? 0.6 : 1,
                }}
              >
                <FaFilePdf />{" "}
                {t("projectReports.printPDF", { defaultValue: "طباعة PDF" })}
              </button>
              <button
                onClick={() => setIsPreviewOpen(false)}
                style={{
                  backgroundColor: "#dc3545",
                  color: "white",
                  border: "none",
                  padding: "10px 15px",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "18px",
                }}
              >
                <FaTimes />
              </button>
            </div>

            <div
              style={{
                transform: `translate(${previewPosition.x}px, ${previewPosition.y}px) scale(${previewZoom})`,
                transformOrigin: "center center",
                cursor: isDragging ? "grabbing" : "grab",
                transition: isDragging ? "none" : "transform 0.1s",
                display: "flex",
                flexDirection: "column",
                gap: "20px",
                alignItems: "center",
              }}
              onMouseDown={e => {
                if (e.button === 0) {
                  setIsDragging(true);
                  setDragStart({
                    x: e.clientX - previewPosition.x,
                    y: e.clientY - previewPosition.y,
                  });
                }
              }}
            >
              {allTasksForPreview &&
                allTasksForPreview.length > 0 &&
                (() => {
                  const previewChunks = chunkArray(allTasksForPreview, 18);
                  return previewChunks.map((chunk, chunkIdx) => (
                    <div
                      key={chunkIdx}
                      id={`preview-tasks-report-pdf-all-${chunkIdx}`}
                      style={{
                        width: "794px",
                        height: "1123px",
                        backgroundColor: "#ffffff",
                        color: "#000000",
                        fontFamily:
                          '"Times New Roman Bold", "Times New Roman", "Times", serif',
                        fontWeight: "bold",
                        padding: "40px",
                        boxSizing: "border-box",
                        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.5)",
                      }}
                    >
                      <div
                        style={{
                          border: "5px solid #000",
                          padding: "3px",
                          backgroundColor: "#ffffff",
                          boxSizing: "border-box",
                          width: "100%",
                          height: "1043px",
                          overflow: "hidden",
                        }}
                      >
                        <div
                          style={{
                            border: "2px solid #000",
                            minHeight: "1033px",
                            height: "1033px",
                            padding: "18px 18px 10px",
                            position: "relative",
                            display: "flex",
                            flexDirection: "column",
                            fontFamily:
                              '"Times New Roman Bold", "Times New Roman", "Times", serif',
                            fontWeight: "bold",
                            WebkitFontSmoothing: "antialiased",
                            MozOsxFontSmoothing: "grayscale",
                            textRendering: "optimizeLegibility",
                            direction: dir,
                            backgroundColor: "#ffffff",
                            boxSizing: "border-box",
                            width: "100%",
                            overflow: "hidden",
                          }}
                        >
                          <div
                            style={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              width: "100%",
                              height: "100%",
                              backgroundImage: `url(${headerLogo})`,
                              backgroundSize: "95%",
                              backgroundPosition: "center center",
                              backgroundRepeat: "no-repeat",
                              opacity: 0.08,
                              zIndex: 0,
                              pointerEvents: "none",
                            }}
                          />
                          <div
                            style={{
                              position: "relative",
                              zIndex: 1,
                              display: "flex",
                              flexDirection: "column",
                              height: "100%",
                              minHeight: "100%",
                            }}
                          >
                            <img
                              src={`${publicUrl}/HeaderAce.png`}
                              alt="Header"
                              style={{
                                width: "100%",
                                height: "auto",
                                objectFit: "contain",
                                marginBottom: "8px",
                                borderBottom: "4px double #000",
                                paddingBottom: "8px",
                                flexShrink: 0,
                              }}
                              crossOrigin="anonymous"
                            />

                            <div
                              style={{
                                flex: 1,
                                display: "flex",
                                flexDirection: "column",
                                overflow: "hidden",
                                minHeight: 0,
                                paddingBottom: "120px",
                              }}
                            >
                              <div
                                style={{
                                  marginTop: "12px",
                                  textAlign: "center",
                                  marginBottom: "20px",
                                  flexShrink: 0,
                                }}
                              >
                                <h2
                                  style={{
                                    fontSize: "20px",
                                    fontWeight: "700",
                                    color: "#000080",
                                    margin: 0,
                                  }}
                                >
                                  {t("projectReports.tasks", {
                                    defaultValue: "تقارير المهام",
                                  })}
                                </h2>
                                <p
                                  style={{
                                    fontSize: "12px",
                                    margin: "0 0 4px",
                                    color: "#333",
                                    fontFamily: '"Times New Roman", serif',
                                    fontWeight: "normal",
                                  }}
                                >
                                  {new Date().toLocaleDateString(
                                    isRTL ? "ar-SA" : "en-US",
                                    {
                                      year: "numeric",
                                      month: "long",
                                      day: "numeric",
                                    },
                                  )}
                                </p>
                                {(projectId ||
                                  employeeId ||
                                  contractorId ||
                                  status ||
                                  priority ||
                                  fromDate ||
                                  toDate) && (
                                  <div
                                    style={{
                                      fontSize: "11px",
                                      color: "#555",
                                      direction: isRTL ? "rtl" : "ltr",
                                      fontFamily: '"Times New Roman", serif',
                                      fontWeight: "normal",
                                    }}
                                  >
                                    {[
                                      selectedProject &&
                                        `${t("projectReports.project", { defaultValue: "المشروع" })}: ${getProjectName(selectedProject)}`,
                                      selectedEmployee &&
                                        `${t("projectReports.employee", { defaultValue: "الموظف" })}: ${getEmployeeName(selectedEmployee)}`,
                                      selectedContractor &&
                                        `${t("projectReports.contractor", { defaultValue: "المقاول" })}: ${getContractorName(selectedContractor)}`,
                                      status &&
                                        `${t("projectReports.status", { defaultValue: "الحالة" })}: ${translateTaskStatus(status)}`,
                                      priority &&
                                        `${t("projectReports.priority", { defaultValue: "الأولوية" })}: ${translateTaskPriority(priority)}`,
                                      fromDate &&
                                        `${t("projectReports.fromDate", { defaultValue: "من" })}: ${formatDate(fromDate)}`,
                                      toDate &&
                                        `${t("projectReports.toDate", { defaultValue: "إلى" })}: ${formatDate(toDate)}`,
                                    ]
                                      .filter(Boolean)
                                      .join(" | ")}
                                  </div>
                                )}
                              </div>
                              <div
                                style={{
                                  color: "#000",
                                  fontSize: "12px",
                                  flex: 1,
                                  overflow: "hidden",
                                }}
                              >
                                {chunk && chunk.length > 0 ? (
                                  <table
                                    style={{
                                      width: "100%",
                                      borderCollapse: "collapse",
                                      border: "1px solid #000",
                                      fontSize: "11px",
                                    }}
                                  >
                                    <thead>
                                      <tr
                                        style={{
                                          backgroundColor:
                                            "rgba(238, 242, 247, 0.7)",
                                          fontWeight: "bold",
                                        }}
                                      >
                                        <th
                                          style={{
                                            border: "1px solid #000",
                                            padding: "6px",
                                            textAlign: "center",
                                            width: "36px",
                                          }}
                                        >
                                          #
                                        </th>
                                        <th
                                          style={{
                                            border: "1px solid #000",
                                            padding: "6px",
                                            textAlign: "center",
                                          }}
                                        >
                                          {t("projectReports.taskName", {
                                            defaultValue: "Task Name",
                                          })}
                                        </th>
                                        <th
                                          style={{
                                            border: "1px solid #000",
                                            padding: "6px",
                                            textAlign: "center",
                                          }}
                                        >
                                          {t("projectReports.project", {
                                            defaultValue: "المشروع",
                                          })}
                                        </th>
                                        <th
                                          style={{
                                            border: "1px solid #000",
                                            padding: "6px",
                                            textAlign: "center",
                                          }}
                                        >
                                          {t("projectReports.employee", {
                                            defaultValue: "الموظف",
                                          })}
                                        </th>
                                        <th
                                          style={{
                                            border: "1px solid #000",
                                            padding: "6px",
                                            textAlign: "center",
                                          }}
                                        >
                                          {t("projectReports.contractor", {
                                            defaultValue: "المقاول",
                                          })}
                                        </th>
                                        <th
                                          style={{
                                            border: "1px solid #000",
                                            padding: "6px",
                                            textAlign: "center",
                                          }}
                                        >
                                          {t("projectReports.status", {
                                            defaultValue: "الحالة",
                                          })}
                                        </th>
                                        <th
                                          style={{
                                            border: "1px solid #000",
                                            padding: "6px",
                                            textAlign: "center",
                                          }}
                                        >
                                          {t("projectReports.priority", {
                                            defaultValue: "الأولوية",
                                          })}
                                        </th>
                                        <th
                                          style={{
                                            border: "1px solid #000",
                                            padding: "6px",
                                            textAlign: "center",
                                          }}
                                        >
                                          {t("projectReports.startDate", {
                                            defaultValue: "Start Date",
                                          })}
                                        </th>
                                        <th
                                          style={{
                                            border: "1px solid #000",
                                            padding: "6px",
                                            textAlign: "center",
                                          }}
                                        >
                                          {t("projectReports.endDate", {
                                            defaultValue: "End Date",
                                          })}
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {chunk.slice(0, 18).map((task, idx) => (
                                        <tr
                                          key={task.id || idx}
                                          style={{
                                            backgroundColor:
                                              idx % 2 === 0
                                                ? "rgba(255, 255, 255, 0.7)"
                                                : "rgba(255, 255, 255, 0.5)",
                                          }}
                                        >
                                          <td
                                            style={{
                                              border: "1px solid #000",
                                              padding: "4px",
                                              textAlign: "center",
                                              fontSize: "10px",
                                            }}
                                          >
                                            {chunkIdx * 18 + idx + 1}
                                          </td>
                                          <td
                                            style={{
                                              border: "1px solid #000",
                                              padding: "4px",
                                              textAlign: isRTL
                                                ? "right"
                                                : "left",
                                              fontSize: "10px",
                                            }}
                                          >
                                            {isRTL
                                              ? task.name_ar || task.name_en
                                              : task.name_en ||
                                                task.name_ar ||
                                                "—"}
                                          </td>
                                          <td
                                            style={{
                                              border: "1px solid #000",
                                              padding: "4px",
                                              textAlign: isRTL
                                                ? "right"
                                                : "left",
                                              fontSize: "10px",
                                            }}
                                          >
                                            {isRTL
                                              ? task.project?.name_ar ||
                                                task.project?.name_en
                                              : task.project?.name_en ||
                                                task.project?.name_ar ||
                                                "—"}
                                          </td>
                                          <td
                                            style={{
                                              border: "1px solid #000",
                                              padding: "4px",
                                              textAlign: isRTL
                                                ? "right"
                                                : "left",
                                              fontSize: "10px",
                                            }}
                                          >
                                            {isRTL
                                              ? task.employee?.name_ar ||
                                                task.employee?.name_en
                                              : task.employee?.name_en ||
                                                task.employee?.name_ar ||
                                                "—"}
                                          </td>
                                          <td
                                            style={{
                                              border: "1px solid #000",
                                              padding: "4px",
                                              textAlign: isRTL
                                                ? "right"
                                                : "left",
                                              fontSize: "10px",
                                            }}
                                          >
                                            {isRTL
                                              ? task.contractor?.name_ar ||
                                                task.contractor?.name_en
                                              : task.contractor?.name_en ||
                                                task.contractor?.name_ar ||
                                                "—"}
                                          </td>
                                          <td
                                            style={{
                                              border: "1px solid #000",
                                              padding: "4px",
                                              textAlign: "center",
                                              fontSize: "10px",
                                            }}
                                          >
                                            {translateTaskStatus(task.status) ||
                                              "—"}
                                          </td>
                                          <td
                                            style={{
                                              border: "1px solid #000",
                                              padding: "4px",
                                              textAlign: "center",
                                              fontSize: "10px",
                                            }}
                                          >
                                            {translateTaskPriority(
                                              task.priority,
                                            ) || "—"}
                                          </td>
                                          <td
                                            style={{
                                              border: "1px solid #000",
                                              padding: "4px",
                                              textAlign: "center",
                                              fontSize: "10px",
                                            }}
                                          >
                                            {formatDate(task.start_date)}
                                          </td>
                                          <td
                                            style={{
                                              border: "1px solid #000",
                                              padding: "4px",
                                              textAlign: "center",
                                              fontSize: "10px",
                                            }}
                                          >
                                            {formatDate(task.end_date)}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                ) : (
                                  <div
                                    style={{
                                      textAlign: "center",
                                      padding: "20px",
                                    }}
                                  >
                                    {t("projectReports.noData", {
                                      defaultValue: "لا توجد بيانات",
                                    })}
                                  </div>
                                )}
                              </div>
                            </div>

                            <div
                              style={{
                                position: "absolute",
                                bottom: "10px",
                                left: "18px",
                                right: "18px",
                                flexShrink: 0,
                              }}
                            >
                              <img
                                src={`${publicUrl}/FooterAce.png`}
                                alt="Footer"
                                style={{
                                  width: "100%",
                                  height: "auto",
                                  objectFit: "contain",
                                    borderTop: "4px double #000",
                                    paddingTop: "8px",
                                }}
                                crossOrigin="anonymous"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ));
                })()}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default TasksReport;
